import { Button } from '@neo/index'
import { Separator } from '@neo/primitives/separator'
// Auth0/Okta SSO buttons are temporarily disabled (see SSO_PROVIDERS below) — no successful
// logins in production. Keep the imports commented so re-enabling is a one-line change.
// import { ReactComponent as Auth0Icon } from '@shared/assets/icons/ic-auth0-color.svg'
import { ReactComponent as GoogleIcon } from '@shared/assets/icons/ic-google-color.svg'
import { ReactComponent as MicrosoftIcon } from '@shared/assets/icons/ic-microsoft-color.svg'
// import { ReactComponent as OktaIcon } from '@shared/assets/icons/ic-okta-color.svg'
import { LoaderCircle } from 'lucide-react'
import type React from 'react'

const LAST_USED_SSO_PROVIDER_KEY = 'lastUsedSsoProvider'

export type SsoProviderId = 'google' | 'azure' | 'okta' | 'auth0'

interface SsoProvider {
  id: SsoProviderId
  label: string
  Icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>
}

// `id` is the sso_type sent to hodor's /v1/authorize. Azure uses the Microsoft logo/label.
// Auth0/Okta disabled until accounts are provisioned — they produced 0 successful logins in 2
// weeks (every callback failed "user not found in AMS"). Backend routes/config remain in place;
// uncomment these entries (and their icon imports above) to re-enable.
const SSO_PROVIDERS: SsoProvider[] = [
  { id: 'google', label: 'Login with Google', Icon: GoogleIcon },
  { id: 'azure', label: 'Login with Microsoft', Icon: MicrosoftIcon },
  // { id: 'auth0', label: 'Login with Auth0', Icon: Auth0Icon },
  // { id: 'okta', label: 'Login with Okta', Icon: OktaIcon },
]

const GOOGLE_PROVIDER = SSO_PROVIDERS[0]

const getLastUsedProvider = (): SsoProviderId => {
  const stored = window.localStorage.getItem(LAST_USED_SSO_PROVIDER_KEY)
  return SSO_PROVIDERS.some((p) => p.id === stored) ? (stored as SsoProviderId) : 'google'
}

interface SsoProviderButtonsProps {
  onSelect: (ssoType: SsoProviderId) => void
  /** True while an authorize request is in flight (any provider). */
  isPending?: boolean
  /** sso_type currently being authorized — drives which button shows the spinner. */
  pendingProvider?: SsoProviderId
}

interface ProviderButtonProps {
  provider: SsoProvider
  isPending?: boolean
  pendingProvider?: SsoProviderId
  onSelect: (id: SsoProviderId) => void
}

const ProviderButton = ({
  provider,
  isPending,
  pendingProvider,
  onSelect,
}: ProviderButtonProps) => {
  const { id, label, Icon } = provider
  const loading = isPending && pendingProvider === id
  return (
    <Button
      variant="outline"
      typography="action"
      className="flex h-12 w-full justify-start border-st2 p-0 text-left hover:border-brand hover:bg-background hover:text-primary after:hidden"
      disabled={isPending}
      disabledReason={isPending ? 'Sign-in in progress, please wait.' : undefined}
      onClick={() => onSelect(id)}
      data-testid={`login-sso-${id}-btn`}
      aria-label={label}
    >
      <span className="flex size-12 shrink-0 items-center justify-center">
        {loading ? <LoaderCircle className="h-5 w-5 animate-spin" /> : <Icon className="size-6" />}
      </span>
      <Separator orientation="vertical" className="h-full" />
      <span className="ml-3">{label}</span>
    </Button>
  )
}

export const SsoProviderButtons = ({
  onSelect,
  isPending,
  pendingProvider,
}: SsoProviderButtonsProps) => {
  const lastUsed = getLastUsedProvider()
  const primary = SSO_PROVIDERS.find((p) => p.id === lastUsed) ?? GOOGLE_PROVIDER
  // Last-used provider first, then the rest — all shown directly (no expand/collapse).
  const ordered = [primary, ...SSO_PROVIDERS.filter((p) => p.id !== primary.id)]

  const handleSelect = (id: SsoProviderId) => {
    window.localStorage.setItem(LAST_USED_SSO_PROVIDER_KEY, id)
    onSelect(id)
  }

  return (
    <div className="flex flex-col gap-2">
      {ordered.map((provider) => (
        <ProviderButton
          key={provider.id}
          provider={provider}
          isPending={isPending}
          pendingProvider={pendingProvider}
          onSelect={handleSelect}
        />
      ))}
    </div>
  )
}
